import { useState, useRef, useEffect } from "react";
import { MyContainer } from "@/components/MyContainer";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/api/api-client";
import { fbProductCodes, fbVariantCodes } from "@/api/fbapi";

type CallRecord = {
    id: number;
    text: string;
    status: "loading" | "ok" | "error";
    statusCode?: number;
    durationMs?: number;
    request: Record<string, unknown>;
    response: unknown;
    timestamp: string;
};

type CustomCase = {
    id: string;
    text: string;
    expect: 'ok' | 'fail';
    note?: string;
};

const STORAGE_KEY = 'detection_custom_cases';

function loadCustomCases(): CustomCase[] {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); }
    catch { return []; }
}
function saveCustomCases(cases: CustomCase[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
}

export default function DetectionTestPage() {
    const [inputText, setInputText] = useState("");
    const [records, setRecords] = useState<CallRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const counter = useRef(0);

    // ── Code reference state ─────────────────────────────────────────────────
    const [productCodes, setProductCodes] = useState<string[]>([]);
    const [variantCodes, setVariantCodes] = useState<string[]>([]);
    const [codeSearch, setCodeSearch] = useState("");
    const [codesLoading, setCodesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'product' | 'variant'>('product');

    const activeCodes = activeTab === 'product' ? productCodes : variantCodes;
    const filteredCodes = codeSearch.trim()
        ? activeCodes.filter(c => c.toLowerCase().includes(codeSearch.toLowerCase()))
        : activeCodes;

    useEffect(() => {
        setCodesLoading(true);
        Promise.all([fbProductCodes(), fbVariantCodes()])
            .then(([pc, vc]) => {
                setProductCodes(pc.data ?? []);
                setVariantCodes(vc.data ?? []);
            })
            .catch(console.error)
            .finally(() => setCodesLoading(false));
    }, []);

    // ── Custom test cases (localStorage) ────────────────────────────────────
    const [customCases, setCustomCases] = useState<CustomCase[]>(loadCustomCases);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCaseText, setNewCaseText] = useState('');
    const [newCaseExpect, setNewCaseExpect] = useState<'ok' | 'fail'>('ok');
    const [newCaseNote, setNewCaseNote] = useState('');

    const addCustomCase = () => {
        if (!newCaseText.trim()) return;
        const updated = [...customCases, {
            id: Date.now().toString(),
            text: newCaseText.trim(),
            expect: newCaseExpect,
            note: newCaseNote.trim() || undefined,
        }];
        setCustomCases(updated);
        saveCustomCases(updated);
        setNewCaseText('');
        setNewCaseNote('');
        setShowAddForm(false);
    };

    const removeCustomCase = (id: string) => {
        const updated = customCases.filter(c => c.id !== id);
        setCustomCases(updated);
        saveCustomCases(updated);
    };

    const handleSend = async () => {
        if (!inputText.trim()) return;

        const id = ++counter.current;
        const requestPayload = { text: inputText.trim() };
        const timestamp = new Date().toLocaleTimeString();
        const startTime = performance.now();

        // Add a loading placeholder immediately
        const loadingRecord: CallRecord = {
            id,
            text: inputText.trim(),
            status: "loading",
            request: requestPayload,
            response: null,
            timestamp,
        };
        setRecords((prev) => [loadingRecord, ...prev]);
        setIsLoading(true);

        try {
            const response = await apiClient.post<unknown, typeof requestPayload>(
                "/fb/detect-comment",
                requestPayload
            );
            const durationMs = Math.round(performance.now() - startTime);

            setRecords((prev) =>
                prev.map((r) =>
                    r.id === id
                        ? {
                            ...r,
                            status: response.success ? "ok" : "error",
                            durationMs,
                            response,
                        }
                        : r
                )
            );
        } catch (err: any) {
            const durationMs = Math.round(performance.now() - startTime);
            setRecords((prev) =>
                prev.map((r) =>
                    r.id === id
                        ? {
                            ...r,
                            status: "error",
                            durationMs,
                            response: { error: String(err?.message ?? err) },
                        }
                        : r
                )
            );
        } finally {
            setIsLoading(false);
        }
    };

    const statusBadge = (status: CallRecord["status"]) => {
        if (status === "loading")
            return (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 animate-pulse">
                    ⏳ Loading…
                </span>
            );
        if (status === "ok")
            return (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                    ✅ OK
                </span>
            );
        return (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                ❌ Error
            </span>
        );
    };

    return (
        <MyContainer
            title="🧪 Comment Detection Test"
            subtitle="Simulate a Facebook comment and inspect the backend detection response"
            enableBack={true}
        >
            {/* ── Input Panel ── */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                    💬 Simulated Comment Text
                </label>
                <textarea
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    rows={4}
                    placeholder={`e.g. "ខ្ញុំចង់បញ្ជាទិញ ១ ។" or "I want 2 of item A"`}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSend();
                    }}
                />
                <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-slate-400">Ctrl+Enter to send</span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRecords([])}
                            disabled={records.length === 0}
                        >
                            Clear History
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSend}
                            disabled={isLoading || !inputText.trim()}
                            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {isLoading ? "⏳ Sending…" : "▶ Send Comment"}
                        </Button>
                    </div>
                </div>

                {/* ── Categorized Test Suite ── */}
                <details open className="mt-4 border border-slate-200 rounded-2xl overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-2.5 bg-slate-50 cursor-pointer select-none list-none hover:bg-slate-100 transition">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Test Cases</p>
                        <div className="flex items-center gap-2" onClick={e => e.preventDefault()}>
                            <button
                                onClick={e => { e.stopPropagation(); setShowAddForm(v => !v); }}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition"
                            >
                                {showAddForm ? '✕ Cancel' : '+ Add Case'}
                            </button>
                            <span className="text-xs text-slate-400">▾</span>
                        </div>
                    </summary>
                    <div className="p-3 space-y-2">

                        {/* Add case form */}
                        {showAddForm && (
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                                <p className="text-xs font-semibold text-slate-600">New Custom Test Case</p>
                                <div className="flex gap-2">
                                    {/* Expect toggle */}
                                    <button
                                        onClick={() => setNewCaseExpect(v => v === 'ok' ? 'fail' : 'ok')}
                                        className={`shrink-0 text-xs font-bold px-2.5 py-1.5 rounded-lg border transition ${newCaseExpect === 'ok'
                                            ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                                            : 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                                            }`}
                                        title="Toggle expected result"
                                    >
                                        {newCaseExpect === 'ok' ? '✅ OK' : '❌ Fail'}
                                    </button>
                                    {/* Text input */}
                                    <input
                                        type="text"
                                        value={newCaseText}
                                        onChange={e => setNewCaseText(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') addCustomCase(); }}
                                        placeholder="Comment text to test…"
                                        className="flex-1 text-xs font-mono border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-400 transition"
                                        autoFocus
                                    />
                                </div>
                                {/* Note input */}
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCaseNote}
                                        onChange={e => setNewCaseNote(e.target.value)}
                                        placeholder="Optional note (e.g. 'should detect qty=2')"
                                        className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                    />
                                    <button
                                        onClick={addCustomCase}
                                        disabled={!newCaseText.trim()}
                                        className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40 transition"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}
                        {([
                            {
                                label: "1. Basic Code Only", color: "blue",
                                cases: [
                                    { text: "J10", expect: "ok" },
                                    { text: "j10", expect: "ok" },
                                    { text: "J 10", expect: "ok" },
                                    { text: "J-10", expect: "ok" },
                                    { text: "យក J10", expect: "ok" },
                                    { text: "យកJ10", expect: "ok" },
                                    { text: "Order J10", expect: "ok" },
                                    { text: "Take J10", expect: "ok" },
                                ],
                            },
                            {
                                label: "2. With Quantity", color: "indigo",
                                cases: [
                                    { text: "J10 2", expect: "fail", note: "wrong product" },
                                    { text: "J10 x2", expect: "ok" },
                                    { text: "J10 =2", expect: "fail", note: "qty=1" },
                                    { text: "J10=2", expect: "fail", note: "no order" },
                                    { text: "J10= 2", expect: "fail", note: "wrong product" },
                                    { text: "J10 = 2", expect: "fail", note: "wrong product" },
                                    { text: "J១០=2", expect: "fail" },
                                    { text: "J១0=2", expect: "fail" },
                                    { text: "J1០=2", expect: "fail" },
                                    { text: "J10=២", expect: "fail" },
                                    { text: "J១០=២", expect: "fail" },
                                    { text: "J10 qty 2", expect: "ok" },
                                    { text: "J10 2pcs", expect: "ok" },
                                    { text: "J10 02", expect: "ok" },
                                ],
                            },
                            {
                                label: "3. With Phone", color: "teal",
                                cases: [
                                    { text: "J10 012739293", expect: "ok" },
                                    { text: "J10 2 012739293", expect: "fail", note: "wrong product" },
                                    { text: "J10=2 012739293", expect: "fail", note: "no order" },
                                    { text: "យក J10 012739293", expect: "ok" },
                                    { text: "យកt02 011232112", expect: "ok" },
                                    { text: "J10 012 739 293", expect: "fail", note: "qty=12" },
                                    { text: "J10 012-739-293", expect: "ok" },
                                ],
                            },
                            {
                                label: "4. With Location", color: "green",
                                cases: [
                                    { text: "J10 ភ្នំពេញ", expect: "ok" },
                                    { text: "J10ភ្នំពេញ", expect: "ok" },
                                    { text: "J10 Phnom Penh", expect: "ok" },
                                    { text: "J10 2 ភ្នំពេញ", expect: "fail", note: "wrong product" },
                                    { text: "J10=2 ភ្នំពេញ", expect: "fail", note: "no order" },
                                    { text: "យក J10 កំពង់ចាម", expect: "ok" },
                                    { text: "J10 Siem Reap", expect: "ok" },
                                ],
                            },
                            {
                                label: "5. With Size", color: "orange",
                                cases: [
                                    { text: "J10 S", expect: "ok" },
                                    { text: "J10 M", expect: "ok" },
                                    { text: "J10 L", expect: "ok" },
                                    { text: "J10 XL", expect: "ok" },
                                    { text: "J10 XS", expect: "ok" },
                                    { text: "J10 XXL", expect: "ok" },
                                    { text: "J10 size M", expect: "ok" },
                                    { text: "J10 M 2", expect: "ok" },
                                ],
                            },
                            {
                                label: "6. With Color", color: "purple",
                                cases: [
                                    { text: "J10 Black", expect: "ok" },
                                    { text: "J10 Red", expect: "ok" },
                                    { text: "J10 Blue", expect: "ok" },
                                    { text: "J10 White", expect: "ok" },
                                    { text: "J10 Pink", expect: "ok" },
                                    { text: "J10 ស", expect: "ok" },
                                    { text: "J10 ខ្មៅ", expect: "ok" },
                                    { text: "J10 ក្រហម", expect: "ok" },
                                ],
                            },
                            {
                                label: "7. Size + Color", color: "pink",
                                cases: [
                                    { text: "J10 S Black", expect: "ok" },
                                    { text: "J10 M White", expect: "ok" },
                                    { text: "J10 L Red", expect: "ok" },
                                    { text: "J10 XL Blue", expect: "ok" },
                                    { text: "J10 ស XS", expect: "ok" },
                                    { text: "J10 ខ្មៅ L", expect: "ok" },
                                    { text: "J10 M ខ្មៅ", expect: "ok" },
                                    { text: "J10 Red XL", expect: "ok" },
                                ],
                            },
                            {
                                label: "8. Full Combo", color: "amber",
                                cases: [
                                    { text: "J10 M Black 2 012739293 ភ្នំពេញ", expect: "ok" },
                                    { text: "J10 XL Red x2 012739293", expect: "ok" },
                                    { text: "យក J10 S White 1 012739293 ភ្នំពេញ", expect: "ok" },
                                    { text: "J10=2 L ខ្មៅ 012739293", expect: "ok" },
                                    { text: "J10 M 2pcs 012739293 Siem Reap", expect: "ok" },
                                    { text: "J10 S Pink 3 PP 012739293", expect: "ok" },
                                ],
                            },
                            {
                                label: "9. Messy / Real World", color: "rose",
                                cases: [
                                    { text: "j102", expect: "ok" },
                                    { text: "J10mblack2", expect: "ok" },
                                    { text: "J10-2-012739293", expect: "ok" },
                                    { text: "J10 M2 012739293", expect: "ok" },
                                    { text: "J10 012739293 2", expect: "ok" },
                                    { text: "012739293 J10", expect: "ok" },
                                    { text: "J10 pls 2", expect: "ok" },
                                    { text: "want J10 M", expect: "ok" },
                                    { text: "J10 available?", expect: "ok" },
                                    { text: "J10 M black pp here is my some test cases", expect: "ok" },
                                ],
                            },
                        ] as { label: string; color: string; cases: { text: string; expect: string; note?: string }[] }[]).map(group => {
                            const colorMap: Record<string, { bg: string; text: string; border: string; headerBg: string }> = {
                                blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", headerBg: "bg-blue-600" },
                                indigo: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", headerBg: "bg-indigo-600" },
                                teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200", headerBg: "bg-teal-600" },
                                green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", headerBg: "bg-green-600" },
                                orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", headerBg: "bg-orange-600" },
                                purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", headerBg: "bg-purple-600" },
                                pink: { bg: "bg-pink-50", text: "text-pink-700", border: "border-pink-200", headerBg: "bg-pink-600" },
                                amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", headerBg: "bg-amber-600" },
                                rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", headerBg: "bg-rose-600" },
                            };
                            const c = colorMap[group.color] ?? colorMap.blue;
                            return (
                                <details key={group.label} className={`rounded-xl border ${c.border} overflow-hidden`}>
                                    <summary className={`flex items-center justify-between px-3 py-2 cursor-pointer select-none ${c.bg} ${c.text} text-xs font-semibold hover:brightness-95 transition`}>
                                        <span>{group.label}</span>
                                        <span className="flex items-center gap-2">
                                            <span className="opacity-60">{group.cases.filter(c => c.expect === 'fail').length > 0 && `${group.cases.filter(c => c.expect === 'fail').length} known ❌`}</span>
                                            <span className="opacity-50">{group.cases.length} cases ▾</span>
                                        </span>
                                    </summary>
                                    <div className="flex flex-wrap gap-1.5 p-3 bg-white">
                                        {group.cases.map(tc => (
                                            <button
                                                key={tc.text}
                                                title={tc.note ? `Known issue: ${tc.note}` : tc.text}
                                                onClick={() => setInputText(tc.text)}
                                                className={`relative text-xs font-mono px-2.5 py-1 rounded-full border transition hover:scale-105 active:scale-95 ${tc.expect === 'fail'
                                                    ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                                                    : `${c.bg} ${c.border} ${c.text} hover:brightness-95`
                                                    }`}
                                            >
                                                {tc.expect === 'fail' ? '❌ ' : ''}{tc.text}
                                                {tc.note && <span className="ml-1 opacity-60 text-[10px]">({tc.note})</span>}
                                            </button>
                                        ))}
                                    </div>
                                </details>
                            );
                        })}

                        {/* ── Custom Cases Group ── */}
                        <details open={customCases.length > 0} className="rounded-xl border border-slate-300 overflow-hidden">
                            <summary className="flex items-center justify-between px-3 py-2 cursor-pointer select-none bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 transition">
                                <span>✏️ Custom</span>
                                <span className="opacity-50">{customCases.length} cases ▾</span>
                            </summary>
                            <div className="flex flex-wrap gap-1.5 p-3 bg-white min-h-[48px]">
                                {customCases.length === 0 ? (
                                    <p className="text-xs text-slate-400 self-center">No custom cases yet — click <strong>+ Add Case</strong> above</p>
                                ) : (
                                    customCases.map(tc => (
                                        <div key={tc.id} className="flex items-center gap-0.5 group">
                                            <button
                                                title={tc.note || tc.text}
                                                onClick={() => setInputText(tc.text)}
                                                className={`text-xs font-mono px-2.5 py-1 rounded-l-full border-y border-l transition hover:scale-105 active:scale-95 ${tc.expect === 'fail'
                                                    ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                                                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {tc.expect === 'fail' ? '❌ ' : ''}{tc.text}
                                                {tc.note && <span className="ml-1 opacity-50 text-[10px]">({tc.note})</span>}
                                            </button>
                                            <button
                                                title="Remove this case"
                                                onClick={() => removeCustomCase(tc.id)}
                                                className="text-[10px] px-1.5 py-1 rounded-r-full border-y border-r border-slate-200 bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition opacity-0 group-hover:opacity-100"
                                            >✕</button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </details>
                    </div>
                </details>

            </div>

            {/* ── Code Reference Panel ── */}
            <details className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden group">
                {/* Header / toggle */}
                <summary className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50 cursor-pointer select-none list-none hover:bg-slate-100 transition">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-700">📋 Code Reference</span>
                        {/* Tabs — stop click propagating to summary */}
                        <div className="flex rounded-lg overflow-hidden border border-slate-200" onClick={e => e.preventDefault()}>
                            <button
                                onClick={e => { e.stopPropagation(); setActiveTab('product'); }}
                                className={`px-3 py-1 text-xs font-semibold transition ${activeTab === 'product'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-500 hover:bg-slate-50'
                                    }`}
                            >
                                Product
                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${activeTab === 'product' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                    {activeTab === 'product' ? filteredCodes.length : productCodes.length}
                                    {activeTab === 'product' && codeSearch && `/${productCodes.length}`}
                                </span>
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setActiveTab('variant'); }}
                                className={`px-3 py-1 text-xs font-semibold transition ${activeTab === 'variant'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-white text-slate-500 hover:bg-slate-50'
                                    }`}
                            >
                                Variant
                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${activeTab === 'variant' ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                    {activeTab === 'variant' ? filteredCodes.length : variantCodes.length}
                                    {activeTab === 'variant' && codeSearch && `/${variantCodes.length}`}
                                </span>
                            </button>
                        </div>
                    </div>
                    {/* Search + collapse indicator */}
                    <div className="flex items-center gap-2" onClick={e => e.preventDefault()}>
                        <span className="text-xs text-slate-400">
                            {filteredCodes.length}/{activeCodes.length} shown
                        </span>
                        <input
                            type="text"
                            value={codeSearch}
                            onChange={e => setCodeSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            placeholder="Search codes…"
                            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 w-40 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                        />
                        {codeSearch && (
                            <button
                                onClick={e => { e.stopPropagation(); setCodeSearch(''); }}
                                className="text-xs text-slate-400 hover:text-slate-600"
                            >✕</button>
                        )}
                        <span className="text-xs text-slate-400 ml-1">▾</span>
                    </div>
                </summary>

                {/* Badges grid */}
                <div className="px-5 py-4 max-h-48 overflow-y-auto">
                    {codesLoading ? (
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span className="animate-spin">⏳</span> Loading codes…
                        </div>
                    ) : filteredCodes.length === 0 ? (
                        <p className="text-xs text-slate-400">
                            {codeSearch ? `No codes match "${codeSearch}"` : 'No codes found'}
                        </p>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {filteredCodes.map(code => (
                                <button
                                    key={code}
                                    title={`Click to use "${code}" as comment input`}
                                    onClick={() => setInputText(code)}
                                    className={`text-xs font-mono font-semibold px-2.5 py-1 rounded-full border transition hover:scale-105 active:scale-95 ${activeTab === 'product'
                                        ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                        : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                                        }`}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </details>


            {records.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                    <div className="text-4xl mb-3">🔍</div>
                    <p className="text-sm">No results yet. Send a comment above.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {records.map((record) => (
                        <div
                            key={record.id}
                            className={`rounded-2xl border overflow-hidden shadow-sm ${record.status === "ok"
                                ? "border-green-200"
                                : record.status === "error"
                                    ? "border-red-200"
                                    : "border-yellow-200"
                                }`}
                        >
                            {/* Header */}
                            <div
                                className={`flex items-center justify-between px-4 py-3 ${record.status === "ok"
                                    ? "bg-green-50"
                                    : record.status === "error"
                                        ? "bg-red-50"
                                        : "bg-yellow-50"
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-slate-500 font-mono">
                                        #{record.id}
                                    </span>
                                    {statusBadge(record.status)}
                                    <span className="text-sm font-semibold text-slate-800 truncate max-w-[240px]">
                                        "{record.text}"
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                                    {record.durationMs !== undefined && (
                                        <span className="font-mono">{record.durationMs}ms</span>
                                    )}
                                    <span>{record.timestamp}</span>
                                </div>
                            </div>

                            {/* ── Parsed Summary ── */}
                            {(() => {
                                const resp = record.response as any;
                                const data = resp?.data;
                                const variant = data?.variant;
                                const detect = data?.detectResult;
                                if (!data || record.status !== 'ok') return null;
                                return (
                                    <div className="p-4 border-b border-slate-200 bg-white space-y-3">
                                        {/* Variant card */}
                                        {variant ? (
                                            <div className="flex items-start gap-4 bg-green-50 border border-green-200 rounded-xl p-3">
                                                {/* Image placeholder */}
                                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center shrink-0 text-xl">
                                                    {variant.imageUrl
                                                        ? <img src={variant.imageUrl} alt={variant.name} className="w-full h-full object-cover rounded-lg" />
                                                        : '📦'}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-bold text-green-900 text-sm">{variant.productName}</span>
                                                        {variant.name !== variant.productName && (
                                                            <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{variant.name}</span>
                                                        )}
                                                        <span className="text-xs font-mono bg-white border border-green-200 text-green-800 px-2 py-0.5 rounded-full">SKU: {variant.sku}</span>
                                                        {variant.pinned && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">📌 Pinned</span>}
                                                    </div>
                                                    {/* Price */}
                                                    <p className="text-xs text-green-700 mt-1">
                                                        Price: <strong>${variant.price}</strong>
                                                        {variant.cost ? <span className="ml-2 text-slate-500">Cost: ${variant.cost}</span> : null}
                                                    </p>
                                                    {/* Color & Size */}
                                                    <div className="flex gap-2 mt-1.5 flex-wrap">
                                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${variant.color ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-gray-100 border-gray-200 text-gray-400'}`}>
                                                            🎨 Color: {variant.color ?? 'none'}
                                                        </span>
                                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${variant.size ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-100 border-gray-200 text-gray-400'}`}>
                                                            📐 Size: {variant.size ?? 'none'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Found badge */}
                                                <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-green-600 text-white">✅ Found</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3">
                                                <span className="text-2xl">🔍</span>
                                                <div>
                                                    <p className="text-sm font-bold text-red-800">No variant found</p>
                                                    <p className="text-xs text-red-600">Input: "{data?.text}"</p>
                                                </div>
                                                <span className="ml-auto shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-red-500 text-white">❌ Not Found</span>
                                            </div>
                                        )}

                                        {/* DetectResult badges — new schema */}
                                        {detect && (
                                            <div className="space-y-2">
                                                {/* Row 1: Core intent flags */}
                                                <div className="flex flex-wrap gap-2">
                                                    {/* found */}
                                                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${detect.found ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                        {detect.found ? '✅' : '❌'} Found
                                                    </span>
                                                    {/* order intent */}
                                                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${detect.order ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                        {detect.order ? '🛒' : '—'} Order: {detect.order ? 'Yes' : 'No'}
                                                    </span>
                                                    {/* intent label */}
                                                    {detect.intent && (
                                                        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${detect.intent === 'ORDER' ? 'bg-blue-100 text-blue-700 border-blue-300'
                                                            : detect.intent === 'ENQUIRY' ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                                                                : 'bg-slate-100 text-slate-600 border-slate-300'
                                                            }`}>
                                                            🎯 Intent: {detect.intent}
                                                        </span>
                                                    )}
                                                    {/* qty */}
                                                    <span className="text-xs font-bold px-3 py-1 rounded-full border bg-blue-100 text-blue-700 border-blue-300">
                                                        🔢 Qty: {detect.qty ?? 1}
                                                    </span>
                                                    {/* code */}
                                                    {detect.code && (
                                                        <span className="text-xs font-mono px-3 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-300">
                                                            🏷️ Code: {detect.code}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Row 2: Extracted attributes */}
                                                <div className="flex flex-wrap gap-2">
                                                    {/* color from detect */}
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${detect.color ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-gray-100 border-gray-200 text-gray-400'}`}>
                                                        🎨 Req. Color: {detect.color ?? 'none'}
                                                    </span>
                                                    {/* size from detect */}
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${detect.size ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-100 border-gray-200 text-gray-400'}`}>
                                                        📐 Req. Size: {detect.size ?? 'none'}
                                                    </span>
                                                    {/* phone */}
                                                    {detect.phone && (
                                                        <span className="text-xs font-mono px-3 py-1 rounded-full border bg-teal-100 text-teal-700 border-teal-300">
                                                            📞 {detect.phone}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Row 3: Error / text extracted */}
                                                {(detect.error || detect.text) && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {detect.text && (
                                                            <span className="text-xs px-3 py-1 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                                                                💬 Extracted: "{detect.text}"
                                                            </span>
                                                        )}
                                                        {detect.error && (
                                                            <span className="text-xs font-bold px-3 py-1 rounded-full border bg-red-100 text-red-700 border-red-300">
                                                                ⚠️ {detect.error}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    </div>
                                );
                            })()}

                            {/* ── Raw JSON (collapsible) ── */}
                            <details className="group">
                                <summary className="cursor-pointer px-4 py-2 text-xs font-semibold text-slate-500 bg-slate-50 hover:bg-slate-100 transition select-none border-t border-slate-200 flex items-center gap-2">
                                    <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                                    Raw JSON (request / response)
                                </summary>
                                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                                    <div className="p-4 bg-slate-50">
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">📤 Request</p>
                                        <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap break-all bg-white border border-slate-200 rounded-lg p-3">
                                            {JSON.stringify(record.request, null, 2)}
                                        </pre>
                                    </div>
                                    <div className="p-4">
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">📥 Response</p>
                                        <pre className={`text-xs font-mono whitespace-pre-wrap break-all rounded-lg p-3 border ${record.status === 'ok' ? 'bg-green-50 border-green-200 text-green-900'
                                            : record.status === 'error' ? 'bg-red-50 border-red-200 text-red-900'
                                                : 'bg-yellow-50 border-yellow-200 text-yellow-900'
                                            }`}>
                                            {record.status === 'loading' ? 'Waiting…' : JSON.stringify(record.response, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </details>
                        </div>
                    ))}
                </div>
            )}
        </MyContainer>
    );
}
